import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  TelegramLogo, CheckCircle, Warning, Copy, Trash, PaperPlaneTilt, Link as LinkIcon,
  Robot, Users as UsersIcon, ArrowRight,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none text-sm";

export default function Telegram() {
  const [status, setStatus] = useState({ connected: false });
  const [contacts, setContacts] = useState([]);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [testChat, setTestChat] = useState("");
  const [testMsg, setTestMsg] = useState("Teste do Telegram MercadoPagu ✅");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [{ data: st }, { data: cx }] = await Promise.all([
        api.get("/telegram/status"),
        api.get("/telegram/contacts").catch(() => ({ data: { contacts: [] } })),
      ]);
      setStatus(st);
      setContacts(cx.contacts || []);
    } catch (e) { console.warn(e); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!token || !token.includes(":")) return toast.error("Cole o token completo (formato 12345:ABC...)");
    setSaving(true);
    try {
      const { data } = await api.post("/telegram/bot-token", { bot_token: token });
      toast.success(`Bot @${data.bot_username} conectado!`);
      setToken("");
      load();
    } catch (e) { toast.error(errMsg(e, "Erro ao conectar bot")); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Desconectar bot Telegram? Você precisará reconectar para enviar novas mensagens.")) return;
    await api.delete("/telegram/bot-token");
    toast.success("Bot desconectado");
    load();
  };

  const send = async () => {
    if (!testChat || !testMsg) return toast.error("Preencha chat_id e mensagem");
    setSending(true);
    try {
      const { data } = await api.post("/telegram/test", { chat_id: testChat, message: testMsg });
      if (data.result?.mock) {
        toast.error(`Falhou: ${data.result?.error || "erro desconhecido"}`);
      } else {
        toast.success("Mensagem enviada!");
      }
    } catch (e) { toast.error(errMsg(e, "Erro ao enviar")); }
    finally { setSending(false); }
  };

  return (
    <div className="fadeUp max-w-4xl">
      <div className="label-caps mb-2">Comunicação</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3 flex items-center gap-3">
        <TelegramLogo size={44} color="#229ED9" weight="fill" /> Telegram Bot
      </h1>
      <p className="text-white/60 mb-8">
        Envie cobranças automáticas de renovação pelo Telegram — sem risco de banimento,
        gratuito e ilimitado. Ideal para IPTV, cursos e assinaturas.
      </p>

      {/* Status */}
      <div className="card-flat p-6 mb-6" data-testid="tg-status-card">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-sm border flex items-center justify-center shrink-0 ${
            status.connected ? "border-[var(--success)] text-[var(--success)]" : "border-[var(--border)] text-white/40"
          }`}>
            {status.connected ? <CheckCircle size={26} weight="fill" /> : <Warning size={26} />}
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-xl" data-testid="tg-state-label">
              {status.connected ? `Bot @${status.bot_username} conectado` : "Não conectado"}
            </div>
            <div className="text-xs text-white/50">
              {status.connected ? (
                <>Bot: <b className="text-white/80">{status.bot_first_name}</b> · Token: <code className="text-[var(--accent)]">{status.preview}</code></>
              ) : (
                "Conecte um bot para começar a enviar cobranças pelo Telegram"
              )}
            </div>
          </div>
          {status.connected && (
            <button onClick={disconnect} data-testid="tg-disconnect"
              className="btn-ghost text-xs text-[var(--danger)] flex items-center gap-1">
              <Trash size={14} /> Desconectar
            </button>
          )}
        </div>
      </div>

      {!status.connected ? (
        <div className="card-flat p-8" data-testid="tg-setup-card">
          <div className="label-caps mb-2">Passo a passo</div>
          <div className="font-display font-bold text-2xl mb-6 flex items-center gap-2">
            <Robot size={26} color="var(--accent)" weight="duotone" /> Criar seu bot em 3 min
          </div>
          <ol className="text-sm text-white/70 space-y-3 mb-6">
            <li>1. Abra o Telegram e converse com <a href="https://t.me/BotFather" target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">@BotFather</a></li>
            <li>2. Envie <code className="text-[var(--accent)] bg-black/30 px-2 py-0.5 rounded">/newbot</code> — escolha nome e username (deve terminar em <code>_bot</code>)</li>
            <li>3. O BotFather te devolverá um <b>token</b> parecido com <code className="text-xs text-white/50">7654321:AAH-abc...xyz</code></li>
            <li>4. Cole o token abaixo — feito 🚀</li>
          </ol>

          <label className="label-caps block mb-2">Token do bot</label>
          <input value={token} onChange={(e) => setToken(e.target.value)}
            data-testid="tg-token-input" className={inp + " font-mono"}
            placeholder="7654321:AAH-abcdefghijklmnop..." />
          <button onClick={connect} disabled={saving} data-testid="tg-connect-btn"
            className="btn-primary mt-4 flex items-center gap-2">
            <LinkIcon size={16} weight="bold" /> {saving ? "Conectando..." : "Conectar bot"}
          </button>
          <div className="mt-4 text-xs text-white/40">
            💡 Após conectar, cada cliente que você cadastrar em <a href="/app/clientes" className="text-[var(--accent)] underline">Clientes</a> ganha um link único para "iniciar" o bot. Quando ele clica, a plataforma vincula o chat automaticamente.
          </div>
        </div>
      ) : (
        <>
          {/* Envio de teste */}
          <div className="card-flat p-6 mb-6" data-testid="tg-test-card">
            <div className="label-caps mb-2">Teste rápido</div>
            <div className="font-display font-bold text-2xl mb-4 flex items-center gap-2">
              <PaperPlaneTilt size={22} color="var(--success)" weight="fill" /> Enviar mensagem
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-2">Chat ID</label>
                <input value={testChat} onChange={(e) => setTestChat(e.target.value)}
                  data-testid="tg-test-chat" className={inp + " font-mono"}
                  placeholder="Ex: 123456789" />
                <div className="mt-1 text-xs text-white/40">
                  Envie /start para <a href={`https://t.me/${status.bot_username}`} target="_blank" rel="noreferrer" className="text-[var(--accent)] underline">@{status.bot_username}</a> e pegue seu chat_id em <a href="https://api.telegram.org/bot{token}/getUpdates" target="_blank" rel="noreferrer" className="text-white/60">getUpdates</a>
                </div>
              </div>
              <div>
                <label className="label-caps block mb-2">Mensagem</label>
                <input value={testMsg} onChange={(e) => setTestMsg(e.target.value)}
                  className={inp} data-testid="tg-test-msg" />
              </div>
            </div>
            <button onClick={send} disabled={sending} data-testid="tg-test-send"
              className="btn-primary mt-4 flex items-center gap-2">
              <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Enviando..." : "Enviar teste"}
            </button>
          </div>

          {/* Contatos que iniciaram o bot */}
          <div className="card-flat">
            <div className="p-4 border-b border-[var(--border)] flex justify-between items-center">
              <div>
                <div className="label-caps flex items-center gap-2"><UsersIcon size={14} /> Contatos</div>
                <div className="font-display font-bold text-lg">Quem já iniciou o bot ({contacts.length})</div>
              </div>
              <a href="/app/clientes" className="btn-ghost text-xs flex items-center gap-1">
                Ir para clientes <ArrowRight size={12} />
              </a>
            </div>
            <div className="divide-y divide-[var(--border)] max-h-[400px] overflow-y-auto">
              {contacts.length === 0 ? (
                <div className="p-6 text-center text-white/50 text-sm" data-testid="tg-no-contacts">
                  Nenhum contato ainda. Compartilhe o link do bot com seus clientes e peça para eles enviarem /start.
                </div>
              ) : contacts.map((c) => (
                <div key={c.chat_id} className="p-4 flex items-center gap-3" data-testid={`tg-contact-${c.chat_id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#229ED9]/20 border border-[#229ED9]/40 flex items-center justify-center">
                    <TelegramLogo size={14} color="#229ED9" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">
                      {c.first_name} {c.last_name || ""} {c.username && <span className="text-white/40 font-mono text-xs">@{c.username}</span>}
                    </div>
                    <div className="text-xs text-white/50 mt-0.5">
                      chat_id: <code className="text-[var(--accent)]">{c.chat_id}</code>
                      {c.linked_subscriber_id && <span className="ml-2 text-[var(--success)]">· vinculado</span>}
                    </div>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(String(c.chat_id)); toast.success("chat_id copiado"); }}
                    className="btn-ghost text-xs" title="Copiar chat_id">
                    <Copy size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
